import { useEffect, useState } from "react";

import { Check, Copy, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { changePassword } from "@/apis/auth/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changePasswordSchema } from "@/features/auth/schemas/auth";
import { generateSecurePassword } from "@/features/staff/utils/generateSecurePassword";

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Cambio de contraseña propio desde el panel: clave actual + nueva.
 * No usa email de reset; todo ocurre en Firebase Auth del cliente.
 */
export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setCopied(false);
    setFieldErrors({});
    setIsPending(false);
  }, [open]);

  const handleGenerate = () => {
    const next = generateSecurePassword();
    setNewPassword(next);
    setConfirmNewPassword(next);
    setShowNew(true);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!newPassword) return;
    await navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmNewPassword,
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !next[key]) {
          next[key] = issue.message;
        }
      }
      setFieldErrors(next);
      return;
    }

    setFieldErrors({});
    setIsPending(true);
    try {
      const result = await changePassword(parsed.data);
      if (!result.success) {
        toast.error(result.error ?? "No se pudo cambiar la contraseña");
        return;
      }
      toast.success("Contraseña actualizada");
      onClose();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" aria-hidden />
            Cambiar mi contraseña
          </DialogTitle>
          <DialogDescription>
            Solo afecta tu cuenta. Ingresá la clave actual y elegí una nueva (sin correo
            de restablecimiento).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="own-current-password">Contraseña actual</Label>
            <div className="relative">
              <Input
                id="own-current-password"
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((prev) => !prev)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showCurrent ? "Ocultar" : "Mostrar"}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {fieldErrors.currentPassword && (
              <p className="text-xs text-destructive">{fieldErrors.currentPassword}</p>
            )}
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="own-new-password">Nueva contraseña</Label>
              <Button type="button" variant="outline" size="sm" onClick={handleGenerate}>
                <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                Generar segura
              </Button>
            </div>
            <div className="relative">
              <Input
                id="own-new-password"
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                className="pr-20"
              />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                <button
                  type="button"
                  onClick={() => void handleCopy()}
                  disabled={!newPassword}
                  className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
                  aria-label="Copiar"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNew((prev) => !prev)}
                  className="p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label={showNew ? "Ocultar" : "Mostrar"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {fieldErrors.newPassword && (
              <p className="text-xs text-destructive">{fieldErrors.newPassword}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="own-confirm-password">Confirmar nueva</Label>
            <Input
              id="own-confirm-password"
              type={showNew ? "text" : "password"}
              value={confirmNewPassword}
              onChange={(event) => setConfirmNewPassword(event.target.value)}
              autoComplete="new-password"
            />
            {fieldErrors.confirmNewPassword && (
              <p className="text-xs text-destructive">{fieldErrors.confirmNewPassword}</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
